import { PrepareApiProcedure } from '../components/common/utils/prepare-api-procedure';
import {
  ActiveChargeStatus,
  ChargerAttributes,
  ChargerManufacturerInfo,
} from '../models/charger-model';
import { OrderAttributes } from '../models/order-model';
import {
  ChargerSchedule,
  SendSchedule,
  setSchedule,
} from '../store/actions/scheduling-actions';
import {
  ApiRequest,
  ApiResponse,
  fetchDataFromAPI,
  fetchDataFromAPITyped,
} from './api-requests';
import type { GetChargerEventsResponse } from '../models/charger-event-model';

export interface CheckinResponse {
  message: string;
  status: 'COMPLETE' | 'PENDING_RECONCILIATION';
  chargerOffline: boolean;
  pendingReconciliation: boolean;
}

export const checkinCharger = async (
  orderId: string
): Promise<ApiResponse<CheckinResponse | null>> => {
  const apiQuery: ApiRequest = {
    route: 'charger/checkin',
    request: {
      method: 'POST',
      entity: 'charger',
      body: {
        orderId: orderId,
      },
    },
  };
  return fetchDataFromAPITyped<CheckinResponse>(apiQuery);
};

export const checkoutCharger = async (
  orderId: string,
  email: string,
  scheduleId?: string | null
) => {
  //Check out the charger using the checkout API
  const d = new Date().getTimezoneOffset();
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const apiQuery: ApiRequest = {
    route: 'charger/checkout',
    request: {
      method: 'POST',
      entity: 'charger',
      body: {
        orderId: orderId,
        tz_offset: d,
        tz: tz,
        email: email,
        scheduleId: scheduleId ?? null,
      },
    },
  };

  return await fetchDataFromAPITyped<null>(apiQuery);
  // TODO: Check the response for success here
};

export const getActiveChargeData = async (orderId: string) => {
  const apiRequest: ApiRequest = {
    route: 'charger/getActiveChargeData',
    request: {
      method: 'POST',
      entity: 'charger',
      body: { orderId: orderId },
    },
  };
  return fetchDataFromAPITyped<ActiveChargeStatus>(apiRequest);
};

export const getUserActiveCharges = async () => {
  const apiRequest: ApiRequest = {
    route: 'user/getActiveCharges',
    request: {
      method: 'GET',
      entity: 'user',
      body: undefined,
    },
  };
  return fetchDataFromAPITyped<any[]>(apiRequest);
};

export const getManufacturerInfoForCharger = async (
  serialNumber: string,
  manufacturer: string
) => {
  const apiRequest: ApiRequest = {
    route: `charger/${serialNumber}/manufacturerInfo/${manufacturer}`,
    request: {
      method: 'GET',
      entity: 'charger',
      body: undefined,
    },
  };
  const manufacturerInfo =
    fetchDataFromAPITyped<ChargerManufacturerInfo>(apiRequest);
  return manufacturerInfo;
};

export const getSingleChargerData = async (chargerId: string) => {
  try {
    const apiQuery = PrepareApiProcedure(
      `charger/getSingleCharger`,
      'POST',
      'charger',
      {
        chargerId: chargerId,
      }
    );
    const singleCharger = await fetchDataFromAPI(apiQuery);
    return singleCharger[0];
  } catch (error) {
    console.log(error);
  }
};

export const listMyChargers = async () => {
  try {
    const apiRequest: ApiRequest = {
      route: `charger/getMyChargers`,
      request: {
        method: 'GET',
        entity: 'charger',
        body: undefined,
      },
    };
    const myChargers = fetchDataFromAPITyped<ChargerAttributes[]>(apiRequest);
    return myChargers;
  } catch (error) {
    return null;
  }
};

/** Operational / OCPP-style event log for a charger (host-only). */
export const getChargerEvents = async (
  chargerId: string,
  limit?: number
): Promise<ApiResponse<GetChargerEventsResponse | null>> => {
  const params = new URLSearchParams();
  if (limit !== undefined && limit !== null && !Number.isNaN(Number(limit))) {
    const n = Math.min(500, Math.max(1, Math.floor(Number(limit))));
    params.set('limit', String(n));
  }
  const apiRequest: ApiRequest = {
    route: `charger/events/${encodeURIComponent(chargerId)}`,
    request: {
      method: 'GET',
      entity: 'charger',
      body: undefined,
      searchParams: params.toString() ? params : undefined,
    },
  };
  return fetchDataFromAPITyped<GetChargerEventsResponse>(apiRequest);
};

export const getLastUsedCharger = async () => {
  try {
    const apiRequest: ApiRequest = {
      route: 'charger/getLastUsedCharger',
      request: {
        method: 'GET',
        entity: 'charger',
        body: undefined,
      },
    };
    return fetchDataFromAPITyped<ChargerAttributes>(apiRequest);
  } catch (error) {
    return null;
  }
};

interface ActiveOrderStatus_t {
  message: string;
  orders: OrderAttributes[];
}

export const getActiveOrdersByChargerId = async (chargerId: string) => {
  const apiRequest: ApiRequest = {
    route: `charger/status/activeorders/${chargerId}`,
    request: {
      method: 'GET',
      entity: 'charger',
      body: undefined,
    },
  };
  return fetchDataFromAPITyped<ActiveOrderStatus_t>(apiRequest);
};

// ============================================================================
// Live Activity (iOS Dynamic Island) Token Registration
// ============================================================================

/**
 * Register a Live Activity push token with the backend
 * Called when a Live Activity is started so the server can send real-time updates
 * 
 * @param pushToken - The ActivityKit push token (hex string)
 * @param orderId - The order ID for this charging session
 */
export const registerLiveActivityToken = async (
  pushToken: string,
  orderId: string
): Promise<ApiResponse<{ success: boolean; message: string }>> => {
  const apiRequest: ApiRequest = {
    route: 'charger/liveActivity/register',
    request: {
      method: 'POST',
      entity: 'charger',
      body: {
        orderId,
        pushToken,
      },
    },
  };
  return fetchDataFromAPITyped<{ success: boolean; message: string }>(apiRequest);
};

/**
 * Unregister a Live Activity push token from the backend
 * Called when a Live Activity is ended
 * 
 * @param orderId - The order ID for this charging session
 */
export const unregisterLiveActivityToken = async (
  orderId: string
): Promise<ApiResponse<{ success: boolean; message: string }>> => {
  const apiRequest: ApiRequest = {
    route: 'charger/liveActivity/unregister',
    request: {
      method: 'POST',
      entity: 'charger',
      body: {
        orderId,
      },
    },
  };
  return fetchDataFromAPITyped<{ success: boolean; message: string }>(apiRequest);
};

// ============================================================================
// QR Code
// ============================================================================

/**
 * Fetch the QR code image for a charger as a base64-encoded string.
 * The backend returns { qrCodeBase64: string } where the string is a
 * data URI (e.g. "data:image/png;base64,...") that can be used directly
 * as an Image source.
 *
 * @param chargerId - The UUID of the charger
 */
export const getChargerQRCode = async (
  chargerId: string
): Promise<ApiResponse<{ qrCodeBase64: string } | null>> => {
  const apiRequest: ApiRequest = {
    route: `charger/${chargerId}/qrcode`,
    request: {
      method: 'GET',
      entity: 'charger',
      body: undefined,
    },
  };
  return fetchDataFromAPITyped<{ qrCodeBase64: string }>(apiRequest);
};

/**
 * Request the backend to email the charger's QR code to the host user.
 *
 * @param chargerId - The UUID of the charger
 */
export const emailChargerQRCode = async (
  chargerId: string
): Promise<ApiResponse<{ success: boolean; message: string } | null>> => {
  const apiRequest: ApiRequest = {
    route: `charger/${chargerId}/qrcode/email`,
    request: {
      method: 'POST',
      entity: 'charger',
      body: {},
    },
  };
  return fetchDataFromAPITyped<{ success: boolean; message: string }>(apiRequest);
};

// ============================================================================
// OCPP Simulator Controls (Admin - Stage/Dev only)
// ============================================================================

export type OcppErrorCode =
  | 'GroundFailure'
  | 'HighTemperature'
  | 'OverCurrentFailure'
  | 'OverVoltage'
  | 'PowerMeterFailure'
  | 'ConnectorLockFailure'
  | 'InternalError'
  | 'OtherError';

export interface OcppSimulatorStatus {
  charger_id: string;
  connector_status: string;
  vehicle_connected: boolean;
  vehicle_charging: boolean;
  error_code: string | null;
  // Fields from the simulator daemon
  id?: string;
  state?: string;
  cable_connected?: boolean;
  transaction_id?: string | null;
  meter_value?: number;
  meter_start?: number;
  energy_wh?: number;
  current_power?: number;
  pending_remote_start?: boolean;
  vendor?: string;
  model?: string;
  [key: string]: any;
}

/**
 * Toggle cable connection (plug in / unplug) for a simulated OCPP charger.
 * Calling once connects, calling again disconnects.
 */
export const ocppToggleCable = async (
  chargerSerial: string
): Promise<ApiResponse<{ success: boolean; message: string } | null>> => {
  const apiRequest: ApiRequest = {
    route: `admin/ocpp/chargers/${chargerSerial}/cable`,
    request: {
      method: 'POST',
      entity: 'charger',
      body: {},
    },
  };
  return fetchDataFromAPITyped<{ success: boolean; message: string }>(apiRequest);
};

/**
 * Simulate a fault/error on a simulated OCPP charger.
 */
export const ocppSimulateFault = async (
  chargerSerial: string,
  errorCode: OcppErrorCode
): Promise<ApiResponse<{ success: boolean; message: string } | null>> => {
  const apiRequest: ApiRequest = {
    route: `admin/ocpp/chargers/${chargerSerial}/fault`,
    request: {
      method: 'POST',
      entity: 'charger',
      body: { error_code: errorCode },
    },
  };
  return fetchDataFromAPITyped<{ success: boolean; message: string }>(apiRequest);
};

/**
 * Reset a simulated OCPP charger back to Available status.
 */
export const ocppResetCharger = async (
  chargerSerial: string
): Promise<ApiResponse<{ success: boolean; message: string } | null>> => {
  const apiRequest: ApiRequest = {
    route: `admin/ocpp/chargers/${chargerSerial}/reset`,
    request: {
      method: 'POST',
      entity: 'charger',
      body: {},
    },
  };
  return fetchDataFromAPITyped<{ success: boolean; message: string }>(apiRequest);
};

/**
 * Force stop charging on a simulated OCPP charger.
 */
export const ocppForceStop = async (
  chargerSerial: string
): Promise<ApiResponse<{ success: boolean; message: string } | null>> => {
  const apiRequest: ApiRequest = {
    route: `admin/ocpp/chargers/${chargerSerial}/stop`,
    request: {
      method: 'POST',
      entity: 'charger',
      body: {},
    },
  };
  return fetchDataFromAPITyped<{ success: boolean; message: string }>(apiRequest);
};

/**
 * Get status of all simulated OCPP chargers.
 */
export const ocppGetAllStatus = async (): Promise<ApiResponse<OcppSimulatorStatus[] | null>> => {
  const apiRequest: ApiRequest = {
    route: 'admin/ocpp/status',
    request: {
      method: 'GET',
      entity: 'charger',
      body: undefined,
    },
  };
  return fetchDataFromAPITyped<OcppSimulatorStatus[]>(apiRequest);
};

/**
 * Check health of the OCPP simulator daemon.
 */
export const ocppGetHealth = async (): Promise<ApiResponse<{ status: string } | null>> => {
  const apiRequest: ApiRequest = {
    route: 'admin/ocpp/health',
    request: {
      method: 'GET',
      entity: 'charger',
      body: undefined,
    },
  };
  return fetchDataFromAPITyped<{ status: string }>(apiRequest);
};